import { useLight } from "@/context/LightContext";
import { useEffect, useState, useRef } from "react";

/**
 * Cursor-following spotlight (Hero) that BLOOMS into a soft ambient wash
 * as the user scrolls past the illuminated zone. No hard cutoff — the
 * mask radius grows and the outer darkness lightens on a continuous
 * scroll-progress curve.
 */
export default function SpotlightOverlay() {
    const { activated } = useLight();
    const [progress, setProgress] = useState(0);
    const rafRef = useRef(null);

    useEffect(() => {
        if (!activated) return;
        let ticking = false;
        const update = () => {
            const y = window.scrollY;
            const vh = window.innerHeight;
            // 0 → 1 across the range vh*0.6 → vh*2.2 (starts blooming just after the fold)
            const p = Math.max(0, Math.min(1, (y - vh * 0.6) / (vh * 1.6)));
            setProgress(p);
            ticking = false;
        };
        const onScroll = () => {
            if (!ticking) {
                rafRef.current = requestAnimationFrame(update);
                ticking = true;
            }
        };
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            cancelAnimationFrame(rafRef.current);
        };
    }, [activated]);

    if (!activated) return null;

    // Interpolated values
    const radius = 320 + progress * 900; // 320 → 1220 px
    const innerStop = 45 + progress * 40; // 45% → 85% (softer falloff as it grows)
    const darkAlpha = 0.82 * (1 - progress * 0.9); // 0.82 → 0.08
    const haloAlpha = 0.16 + progress * 0.05;
    const haloRadius = 260 + progress * 700;

    return (
        <>
            {/* Dark mask — softens & fades on scroll */}
            <div
                aria-hidden="true"
                data-testid="spotlight-mask"
                style={{
                    position: "fixed",
                    inset: 0,
                    pointerEvents: "none",
                    zIndex: 40,
                    transition: "background 0.15s linear",
                    background: `radial-gradient(circle ${radius}px at var(--sx, 50vw) var(--sy, 50vh), rgba(0,0,0,0) 0%, rgba(0,0,0,0) ${innerStop * 0.6}%, rgba(10,0,0,${darkAlpha * 0.7}) ${innerStop}%, rgba(10,0,0,${darkAlpha}) 100%)`,
                }}
            />
            {/* Warm gold halo — grows into the ambient glow */}
            <div
                aria-hidden="true"
                data-testid="spotlight-halo"
                style={{
                    position: "fixed",
                    inset: 0,
                    pointerEvents: "none",
                    zIndex: 41,
                    mixBlendMode: "screen",
                    transition: "background 0.15s linear",
                    background: `radial-gradient(circle ${haloRadius}px at var(--sx, 50vw) var(--sy, 50vh), rgba(255,224,140,${haloAlpha}) 0%, rgba(212,175,55,${haloAlpha * 0.5}) 40%, rgba(0,0,0,0) 75%)`,
                }}
            />
        </>
    );
}
